import React, { ReactElement } from "react";
import { Box, Link } from "@chakra-ui/react";
import { Checkbox, Flex, Icon, Text } from "@familyzone/component-library";

import { isNoCookieYoutubeUrl } from "../../../utils/urlUtils";
import Tooltip from "rc-tooltip";
import { colours } from "utils/themeUtils";

export interface Item {
	text: string;
	value: string;
}

export enum ResourceType {
	Url,
	Signature,
}

/** This is not safe to be sent as a DTO as it does not match the API contract. */
export interface ResourceOption {
	label: string;
	/** Indicates whether the `resourceValue` property is a URL or signature. */
	resourceType: ResourceType;
	/** Actual URL or signature. Whether this is a URL or signature is indicated by the `resourceType` property. */
	resourceValue: string;
}

export interface SelectedResource extends ResourceOption {
	openTab: boolean;
	/**
	 * True by defailt. Updated based on server-side validation outcome upon submission.
	 */
	isValid: boolean;

	/**
	 * The ID of the resource. This is only used when updating an existing focus resource.
	 */
	id?: string;
}

interface Props {
	selectedResources: SelectedResource[];
	onRemove: (resource: SelectedResource) => void;
	onCheckboxChanged: (resource: SelectedResource) => void;
}

export function SelectedFocusResources(props: Props): ReactElement {
	if (props.selectedResources.length === 0) {
		return <></>;
	}

	const anyResourcesAreInvalid = props.selectedResources.some((resource) => !resource.isValid);

	return (
		<Box mt={15} data-testid="selected-focus-resources">
			{anyResourcesAreInvalid && (
				<Flex bg={colours.danger.background.weaker} p={15} rounded={"md"} my={15} data-testid="invalid-resource-selected">
					<Box mr={10}>
						<Icon icon="fa-lock" color={colours.red[500]} width="0.8em" height="0.8em" />
					</Box>
					<Box flex={1}>
						<Text color={colours.red[500]} fontWeight={"medium"}>
							Locked rule
						</Text>
						<Text>
							Resources with locked rules are not allowed to be added. Please remove the highlighted resource(s) to continue.{" "}
							<Link
								href="https://help.classwize.net/hc/en-gb/articles/6354876577948-Rules-Validation-Overview#h_01HBFRZWRGP0WXNKSP6DGVNDQ2"
								target="_blank"
							>
								Locked rules
								<Icon icon="fa-arrow-up-right-from-square" width="0.8em" height="0.8em" ml={5} />
							</Link>
						</Text>
					</Box>
				</Flex>
			)}

			<Box border={"1px solid #eeeeee"} rounded={"sm"} mt={20}>
				<Flex alignItems="center" px={15} py={12} fontWeight={500} style={{ boxShadow: "var(--chakra-shadows-20)" }}>
					<Box flex={1}>Resource</Box>
					<Box mx={30}>
						<Flex alignItems="center" justifyContent="center" gap="8px">
							<Text lineHeight="16px" fontWeight="medium">
								Open tab
							</Text>
							<Tooltip
								placement="topRight"
								trigger="hover"
								destroyTooltipOnHide={true}
								overlayClassName="Tooltip"
								overlayStyle={{ pointerEvents: "auto" }}
								overlay={
									<Text>
										The URL will open in a new tab on the student device.{" "}
										<a
											href="https://help.classwize.net/hc/en-gb/articles/11598018402332"
											target="_blank"
											rel="noopener noreferrer"
											style={{ cursor: "pointer", textDecoration: "underline" }}
										>
											Find out more
										</a>
									</Text>
								}
								// The arrow will not be pointing to the icon if we don't offset it
								align={{ offset: [24, 0] }}
							>
								<Icon icon="fa-question-circle" color={colours.blueGray[400]} data-testid={"question"} width="0.8em" height="0.8em" />
							</Tooltip>
						</Flex>
					</Box>
					<Box></Box>
				</Flex>
				{props.selectedResources.map((resource, index) => (
					<Flex
						key={index}
						style={{
							backgroundColor: resource.isValid ? "inherit" : colours.danger.background.weaker,
						}}
						alignItems="center"
						px={15}
						py={12}
					>
						<Box flex={1}>
							<Text overflowWrap={"break-word"} wordBreak={"break-all"}>
								{resource.label}
							</Text>
						</Box>
						<Flex mx={10} alignItems="center">
							<Checkbox
								name="alert"
								isChecked={resource.openTab}
								isDisabled={isNoCookieYoutubeUrl(resource.resourceValue)}
								onChange={() => props.onCheckboxChanged(resource)}
								data-testid={`checkbox-${index}`}
							/>
							<Icon
								ml={30}
								icon="fa-close"
								color={colours.danger.icon}
								cursor="pointer"
								onClick={() => props.onRemove(resource)}
								data-testid={`remove-${index}`}
							/>
						</Flex>
					</Flex>
				))}
			</Box>
		</Box>
	);
}
